import { Popover } from "antd";
import type { UserGroupUserPermission } from "api/interfaces/userInterface.interface";
import DwPermissionCheckList from "pages/users/userGroup/add/steps/DwPermissionCheckList";
import { useEffect, useMemo } from "react";
import type { Option } from "utils/options";
import styled from "styled-components";
import SelectComponent, {
  defaultMultiOptionRender,
} from "components/atoms/select/SelectComponent";
import type { CustomSelectProps } from "components/atoms/select/SelectComponent";

const initialDisabledList: UserGroupUserPermission = {
  enableAllPermissions: true,
  manageAlertRules: true,
  viewAlerts: true,
  manageUsersAndGroups: true,
  viewUsersAndGroups: true,
  manageReportRules: true,
  viewReports: true,
  manageRecordersAndGroups: true,
  viewRecordersAndGroups: true,
  manageAccount: true,
  viewAccount: true,
  view: true,
  manageBillingAndLicenses: true,
  enableDelete: true,
  subscriptionOrganization: true,
  suspend: true,
  lowLevelAccess: true,
  subscriptionKeys: true,
  generateKeys: true,
  serviceAndPricing: true,
  serviceAndPricingUpdate: true,
  cumulus: true,
  generateCumulus: true,
  myOrganization: true,
  videoGuideSetting: true,
  techSupport: true,
  siteNotice: true,
  emails: true,
  viewSubscription: true,
  manageSubscription: true,
  announcement: true,
  logging: true,
  releaseNotes: true,
  manageMaintenance: true,
};

type UserGroupOption = Option & {
  userPermission?: UserGroupUserPermission;
};

type Props = Omit<
  CustomSelectProps<string[]>,
  "mode" | "onChange" | "value" | "defaultValue" | "options"
> & {
  options: Option[];
  value?: string[];
  defaultValue?: string[];
  disabled?: boolean;
  loading?: boolean;
  onChange?: (value: string[]) => void;
};

export default function UserGroupSelect(props: Props) {
  const optionRender = useMemo(() => {
    return (option: any) => {
      const data = option?.data as UserGroupOption | undefined;
      const base = defaultMultiOptionRender(option, props.value ?? []);

      if (!data?.userPermission) return base;

      return (
        <Popover
          placement="right"
          trigger="hover"
          mouseEnterDelay={0.1}
          content={
            <BaseWrapper>
              <DwPermissionCheckList
                userCheckedList={data.userPermission}
                userDisabledList={initialDisabledList}
                onChangeDwPermission={() => null}
              />
            </BaseWrapper>
          }
        >
          <div>{base}</div>
        </Popover>
      );
    };
  }, [props.value]);

  return (
    <SelectComponent
      {...props}
      mode="multiple"
      placeholder={props.placeholder ?? "Select Groups"}
      optionRender={props.optionRender ?? optionRender}
      onChange={(value) => {
        const v = (value as string[]) || [];
        props.onChange?.(v);
      }}
    />
  );
}

const BaseWrapper = styled.div`
  background-color: ${({ theme }) => theme.COLORS.PRIMARY2};
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
`;
